
import React, { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Axios from "axios";
import {
    Grid,
    Typography,
    Button,
    TextField,
    Alert,
    CircularProgress,
    InputAdornment,
    IconButton
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useTranslation } from 'react-i18next';

function PasswordResetConfirm() {
    const { t, i18n } = useTranslation();
    const { config, environment } = require("../../index");
    const { uid, token } = useParams(); // URL-ből jövő paraméterek
    const navigate = useNavigate();

    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const [successMsg, setSuccessMsg] = useState("");

    const resetPasswordConfirmEndpoint = useMemo(
        () => config?.[environment]?.resetPasswordConfirmEndpoint,
        [config, environment]
    );

    const endpointReady = typeof resetPasswordConfirmEndpoint === "string" && resetPasswordConfirmEndpoint.length > 0;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrorMsg("");
        setSuccessMsg("");

        if (!endpointReady) {
            setErrorMsg(t('Administration.EndpointError', "A jelszó visszaállítás jelenleg nem elérhető."));
            return;
        }

        if (!newPassword || !confirmPassword) {
            setErrorMsg(t('Administration.FillAllFields', "Kérlek töltsd ki mindkét mezőt."));
            return;
        }

        if (newPassword.length < 8) {
            setErrorMsg(t('Administration.PasswordError', "A jelszónak legalább 8 karakter hosszúnak kell lennie."));
            return;
        }

        if (newPassword !== confirmPassword) {
            setErrorMsg(t('Administration.PasswordMatchError', "A két jelszó nem egyezik meg."));
            return;
        }

        setLoading(true);

        try {
            // Djoser: POST /users/reset_password_confirm/
            await Axios.post(resetPasswordConfirmEndpoint, {
                uid: uid,
                token: token,
                new_password: newPassword,
                re_new_password: confirmPassword
            });

            setSuccessMsg(t('Administration.PasswordChangedSuccess', "Jelszó sikeresen megváltoztatva! Átirányítás..."));

            // Redirect to login after 2 seconds
            setTimeout(() => {
                navigate(`/${i18n.language}/login`);
            }, 2000);

        } catch (err) {
            console.error("[PasswordResetConfirm] Error:", err);

            const status = err?.response?.status;
            const data = err?.response?.data;

            if (status === 400) {
                if (data?.token) {
                    setErrorMsg(t('Administration.InvalidResetLink', "A link érvénytelen vagy lejárt. Kérj új jelszó visszaállítási linket."));
                } else if (data?.new_password) {
                    setErrorMsg(data.new_password[0] || t('Administration.PasswordRequirementsError', "A jelszó nem felel meg a követelményeknek."));
                } else {
                    setErrorMsg(t('Administration.GenericError', "Hibás adatok. Kérlek próbáld újra."));
                }
            } else {
                setErrorMsg(t('Administration.GenericError', "Hiba történt. Kérlek próbáld újra később."));
            }
        } finally {
            setLoading(false);
        }
    };

    if (!uid || !token) {
        return (
            <Grid container justifyContent="center" sx={{ mt: 5 }}>
                <Grid item xs={12} md={6} sx={{ border: "2px solid black", p: 3 }}>
                    <Alert severity="error">
                        {t('Administration.InvalidResetLink', "Érvénytelen jelszó visszaállítási link.")}
                    </Alert>
                </Grid>
            </Grid>
        );
    }

    return (
        <Grid container justifyContent="center"
              sx={{ mt: 5, marginTop: { xs: '20px', md: '50px', lg: '100px' } }}>
            <Grid item xs={11} sm={10} md={8} lg={6} sx={{ border: "2px solid black", p: { xs: 2, md: 3 } }}>
                <Grid container justifyContent="center">
                    <Typography variant="h4" sx={{ textAlign: 'center', fontSize: { xs: '1.5rem', md: '2.125rem' } }}>{t('Administration.ConfirmPasswordResetTitle', "ÚJ JELSZÓ BEÁLLÍTÁSA")}</Typography>
                </Grid>

                <Grid container sx={{ mt: 2 }}>
                    <Typography variant="body1" align="center">
                        {t('Administration.EnterNewPassword', "Add meg az új jelszavadat.")}
                    </Typography>
                </Grid>

                {!endpointReady && (
                    <Grid container sx={{ mt: 2 }}>
                        <Alert severity="info" sx={{ width: "100%" }}>
                            {t('Administration.EndpointMissing', "A funkció előkészítve, de a backend endpoint még nincs bekötve.")}
                        </Alert>
                    </Grid>
                )}

                {!!successMsg && (
                    <Grid container sx={{ mt: 2 }}>
                        <Alert severity="success" sx={{ width: "100%" }}>
                            {successMsg}
                        </Alert>
                    </Grid>
                )}

                {!!errorMsg && (
                    <Grid container sx={{ mt: 2 }}>
                        <Alert severity="error" sx={{ width: "100%" }}>
                            {errorMsg}
                        </Alert>
                    </Grid>
                )}

                <form onSubmit={handleSubmit}>
                    <Grid container sx={{ mt: 2 }}>
                        <TextField
                            label={t('Administration.NewPassword', "Új jelszó")}
                            type={showPassword ? "text" : "password"}
                            variant="outlined"
                            fullWidth
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            disabled={loading || !!successMsg}
                            required
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>

                    <Grid container sx={{ mt: 2 }}>
                        <TextField
                            label={t('Administration.ConfirmPassword', "Új jelszó megerősítése")}
                            type={showConfirmPassword ? "text" : "password"}
                            variant="outlined"
                            fullWidth
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            disabled={loading || !!successMsg}
                            required
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            edge="end"
                                        >
                                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>

                    <Grid container sx={{ mt: 2 }}>
                        <Button
                            variant="contained"
                            fullWidth
                            type="submit"
                            disabled={!endpointReady || loading || !!successMsg}
                        >
                            {loading ? <CircularProgress size={24} color="inherit" /> : t('Administration.ChangePassword', "JELSZÓ MÓDOSÍTÁSA")}
                        </Button>
                    </Grid>
                </form>

                <Grid container sx={{ mt: 2 }}>
                    <Button
                        variant="outlined"
                        fullWidth
                        onClick={() => navigate(`/${i18n.language}/login`)}
                        disabled={loading}
                    >
                        {t('Administration.BackToLogin')}
                    </Button>
                </Grid>
            </Grid>
        </Grid>
    );
}

export default PasswordResetConfirm;